import { iconButton } from "Components/iconButton";
import { useRef } from "Shared/haunted/CustomHooks";
import i18next from "i18next";
import { html } from "lit-html";
import { unsafeHTML } from "lit-html/directives/unsafe-html";
import { sanitizeInputFieldValue } from "Services/common";
import { useFluentValidator } from "Services/fluent-validator/FluentValidator";
import { useErrorMessage } from "Services/fluent-validator/useErrorMessage";
import { Validation } from "Services/fluent-validator/Validation";
import { useEffectWithoutCleanup, useState } from "Shared/haunted/CustomHooks";
import { useLoader } from "Shared/haunted/useLoader";
import { useReduxState } from "Shared/redux/useReduxState";
import { createAdminPostBody, handleAdminServerResponse } from "ComponentHelpers/AdminTabHelper";
import {
	adminCaptchaTemplate,
	adminErrorTemplate,
	adminRecaptchaErrorTemplate,
	adminTooltipTemplate,
} from "ComponentHelpers/AdminTabTemplates";
import { safeUrl } from "Components/directives/safeUrl";

export interface Props {
	culture: string;
	isActive: boolean;
	type: "check-in" | "manage";
}

export interface AdminVM {
	nameOrEmail: string;
	pnr: string;
}

type FieldNames = keyof AdminVM;

export const useAdminTab = (props: Props) => {
	const [kontent] = useReduxState("kontent");
	const [captchaStatus] = useReduxState("captchaStatus");

	const renderedCaptchaId = useRef<number>(undefined);

	const [loaderElemId] = useState(`admin-tab-${props.type}-${Date.now().toString()}`);
	const [captchaContainerId] = useState(`captcha-container-${props.type}-${Date.now().toString()}`);

	const [isBookingErrorVisible, setIsBookingErrorVisible] = useState<boolean>(false);
	const [isFareLockErrorVisible, setIsFareLockErrorVisible] = useState<boolean>(false);
	const [isUnauthorizedErrorVisible, setIsUnauthorizedErrorVisible] = useState<boolean>(false);
	const [isTooManyJourneysErrorVisible, setIsTooManyJourneysErrorVisible] = useState<boolean>(false);
	const [isAmericanAirlinesErrorVisible, setIsAmericanAirlinesErrorVisible] = useState<boolean>(false);
	const [isValidated, setIsValidated] = useState<boolean>(false);

	// DEVNOTE We need both state and ref, because refs do not trigger re-renders,
	// which are necessary for validation and input updates
	const [vm, setVm] = useState<AdminVM>({ nameOrEmail: "", pnr: "" });

	// DEVNOTE We have to use this as captcha needs a callback function,
	// which closes over the vm state at the time of initialization.
	const vmRef = useRef<AdminVM>(vm);

	const initCaptcha = () => {
		const captchaContainer = document.createElement("div");
		captchaContainer.id = captchaContainerId;
		document.body.appendChild(captchaContainer);
		renderedCaptchaId.current = grecaptcha.render(captchaContainer, {
			sitekey: window.JetSmart.Settings.GoogleReCaptchaSiteKey,
			size: "invisible",
			callback: (token: string) => onSubmit(token),
		});
	};

	const validator = useFluentValidator<FieldNames, AdminVM>({
		vm,
		validated: isValidated,
		validations: [
			Validation.ruleFor("nameOrEmail", (model: AdminVM) => model.nameOrEmail).isRequired(),
			Validation.ruleFor("pnr", (model: AdminVM) => model.pnr).isRequired(),
		],
	});

	const formErrors = useErrorMessage({ errorMessage: validator.getFormMessages(), verticalGap: true });

	const { startLoading, stopLoading } = useLoader(loaderElemId);

	const onSubmit = async (token: string) => {
		const loader = startLoading();
		const baseUrl = window.JetSmart.Settings.BookingUrl + "/api/Booking/RetrieveBookingFromCorp";

		try {
			const response = await fetch(baseUrl, {
				body: createAdminPostBody(vmRef.current, token, props.culture),
				credentials: "omit",
				method: "POST",
				mode: "cors",
			});

			if (response.ok) {
				await handleAdminServerResponse(response, {
					culture: props.culture,
					loader,
					nameOrEmail: vmRef.current.nameOrEmail.trim(),
					pnr: vmRef.current.pnr.trim(),
					renderedCaptchaId: renderedCaptchaId.current,
					setIsAmericanAirlinesErrorVisible,
					setIsBookingErrorVisible,
					setIsFareLockErrorVisible,
					setIsTooManyJourneysErrorVisible,
					setIsUnauthorizedErrorVisible,
				});
			}
		} catch (e) {
			stopLoading(loader);
		}
	};

	const handleInput = (value: string, key: keyof AdminVM) => {
		setVm((prev) => ({ ...prev, [key]: value }));
	};

	const handleSubmit = async () => {
		setIsBookingErrorVisible(false);
		setIsFareLockErrorVisible(false);
		setIsUnauthorizedErrorVisible(false);
		setIsTooManyJourneysErrorVisible(false);
		setIsValidated(true);
		const isValid = await validator.validate();

		if (isValid) grecaptcha.execute(renderedCaptchaId.current);
	};

	const button = iconButton({
		isAction: true,
		icon: html`<i class="jsh-circle-chevron-right1 text-base"></i>`,
		label: i18next.t("Continuar"),
		onClick: handleSubmit,
	});

	const sanitizePnrInputField = (e: KeyboardEvent) => {
		const target = e.target as HTMLInputElement;
		const value = target.value;
		const sanitizedValue = value
			.split("")
			.filter((char) => /[a-zA-Z0-9]/.test(char))
			.join("")
			.substring(0, 6)
			.toUpperCase();
		target.value = sanitizedValue;
		return sanitizedValue;
	};

	useEffectWithoutCleanup(() => {
		vmRef.current = vm;
	}, [vm]);

	useEffectWithoutCleanup(() => {
		setIsBookingErrorVisible(false);
		setIsFareLockErrorVisible(false);
		setIsUnauthorizedErrorVisible(false);
		setIsTooManyJourneysErrorVisible(false);
		setIsValidated(false);
		setVm({ nameOrEmail: "", pnr: "" });
	}, [props.isActive]);

	useEffectWithoutCleanup(() => {
		if (captchaStatus === "loaded" && props.isActive && renderedCaptchaId.current === undefined) {
			initCaptcha();
		}
	}, [captchaStatus, props.isActive]);

	const bookingErrorTemplate = () =>
		isBookingErrorVisible ? adminErrorTemplate(i18next.t("BookingExistenceError")) : "";

	const farelockErrorTemplate = () =>
		isFareLockErrorVisible ? adminErrorTemplate(i18next.t("FareLockError")) : "";

	const unauthorizedErrorTemplate = () =>
		isUnauthorizedErrorVisible ? adminErrorTemplate(i18next.t("UnauthorizedError")) : "";

	const isTooManyJourneysErrorVisibleTemplate = () =>
		isTooManyJourneysErrorVisible ? adminErrorTemplate(i18next.t("TooManyJourneysError")) : "";

	const isAmericanAirlinesErrorVisibleTemplate = () =>
		isAmericanAirlinesErrorVisible
			? adminErrorTemplate(
					i18next.t(
						"Tu reserva fue comprada en American Airlines, por favor visita {{-linkStart}}www.aa.com{{-linkEnd}}.",
						{
							linkStart: "<a class='text-brand-secondary underline md:hover:text-brand-primary md:hover:no-underline' href='http://www.aa.com'>",
							linkEnd: "</a>",
						}
					)
			  )
			: "";

	const errorsContainerTemplate = () => html`
		<div>
			${formErrors.htmlTemplate()} ${adminRecaptchaErrorTemplate(captchaStatus)} ${bookingErrorTemplate()}
			${farelockErrorTemplate()} ${unauthorizedErrorTemplate()} ${isTooManyJourneysErrorVisibleTemplate()}
			${isAmericanAirlinesErrorVisibleTemplate()}
		</div>
	`;

	const nameOrEmailInputTemplate = () => html`
		<div class="group relative">
			<ac-input
				.autoComplete=${"off"}
				.errorMessage=${validator.getMessage("nameOrEmail")}
				.isInvalid=${!validator.isValid("nameOrEmail")}
				.isSmall=${true}
				.placeholder=${i18next.t("Apellido(s) pasajeros o email comprador")}
				.sanitizer=${(e: KeyboardEvent) => sanitizeInputFieldValue(e, "accepted-chars-for-username-or-email")}
				.value=${vm.nameOrEmail}
				.onInput=${(value: string) => handleInput(value, "nameOrEmail")}
			></ac-input>
			${adminTooltipTemplate(i18next.t("admin-modal-name-or-email-tooltip"))}
		</div>
	`;

	const pnrInputTemplate = () => html`
		<div class="group relative">
			<ac-input
				.autoComplete=${"off"}
				.errorMessage=${validator.getMessage("pnr")}
				.isInvalid=${!validator.isValid("pnr")}
				.isSmall=${true}
				.placeholder=${i18next.t("Código de Reserva")}
				.sanitizer=${sanitizePnrInputField}
				.value=${vm.pnr}
				.onInput=${(value: string) => handleInput(value, "pnr")}
			></ac-input>
			${adminTooltipTemplate(i18next.t("admin-modal-pnr-tooltip"))}
		</div>
	`;

	const formTemplate = () => html`
		<div class="my-4 flex flex-col gap-4 md:w-full md:flex-row">
			<div class="md:basis-1/2">${nameOrEmailInputTemplate()}</div>
			<div class="md:basis-1/2">${pnrInputTemplate()}</div>
		</div>
		<div>${errorsContainerTemplate()}</div>
		<div class="flex justify-end">${button.htmlTemplate()}</div>
	`;

	const htmlTemplate = () => {
		const title =
			props.type === "manage"
				? kontent.manageYourFlight.manageYourFlightTest.contents.title.value
				: kontent.checkIn.checkInTest.contents.title.value;

		const text =
			props.type === "manage"
				? kontent.manageYourFlight.manageYourFlightTest.contents.text.value
				: kontent.checkIn.checkInTest.contents.text.value;

		return html`
			<div id=${loaderElemId} class="flex flex-col gap-4 sm:grid sm:grid-cols-[1fr_2fr] sm:p-4">
				<img
					class="h-auto w-full rounded-lg"
					src=${safeUrl(
						props.type === "manage"
							? kontent.manageYourFlight.manageYourFlightTest.contents.widget
							: kontent.checkIn.checkInTest.contents.widget
					)}
				/>
				<div class="sm:flex sm:flex-col sm:gap-4">
					<h1 class="font-emphasis text-[28px]/none text-n-blue sm:mt-6">${unsafeHTML(title)}</h1>
					<div class="text-sm/5 text-n-blue">${unsafeHTML(text)}</div>
					${formTemplate()}
				</div>
				${adminCaptchaTemplate(captchaContainerId)}
			</div>
		`;
	};

	return { htmlTemplate };
};
